import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { toast } from "sonner";

const T = {
  zh: {
    title: "第三方接入说明",
    intro: "第三方系统(如验货系统、自研脚本)使用 API Token 直接调用只读 REST JSON 接口,无需登录网页。",
    steps: "接入步骤",
    step1: "管理员在「API Token」标签页生成 Token(只显示一次),交给对接方。",
    step2: "每次请求在 HTTP 请求头加入 X-API-Key: <token>。",
    step3: "调用下方接口,响应为 JSON;字段名统一为英文 snake_case,内容原样(中文照中文)。",
    baseUrl: "接口地址前缀",
    auth: "认证请求头",
    ep1: "接口一:按合同号读取产品及包装 / 质检要求(验货场景)",
    ep1Desc: "返回合同基本信息及该合同下所有产品行,每行含包装要求与质检要求。合同号精确匹配。",
    ep2: "接口二:产品清单搜索",
    ep2Desc: "支持关键字、筛选与分页,返回与网页产品清单相同的数据结构。",
    pathParams: "路径参数",
    queryParams: "查询参数",
    respFields: "返回字段",
    itemFields: "items[] 每个产品行字段",
    example: "curl 示例",
    errors: "错误码",
    field: "字段",
    desc: "说明",
    copy: "复制",
    copied: "已复制",
    contractNo: "合同号(与 FileMaker 同步下来的合同号一致,区分大小写)",
    q: "关键字;逗号分隔为「或」,空格分隔为「且」",
    q_fields: "限定搜索字段,逗号分隔(如 item_no,upc)",
    contract_no_q: "按合同号筛选",
    invoice_no_q: "按发票号筛选",
    customer_name_q: "按客户名称筛选",
    factory_name_q: "按厂家名称筛选",
    page: "页码,从 1 开始",
    page_size: "每页条数,最大 200",
    e401: "Token 缺失、无效、已吊销或已过期",
    e404: "合同号不存在",
    e422: "参数格式错误",
    inventoryResp: "items(产品行数组)、total(产品总数)、page、page_size、totals(qty / shipping_qty / amount 汇总)",
  },
  en: {
    title: "Third-party Integration Guide",
    intro: "External systems (inspection tools, scripts) call the read-only REST JSON API with an API Token — no web login required.",
    steps: "Getting started",
    step1: "An admin creates a Token on the \"API Tokens\" tab (shown once) and hands it to the integrator.",
    step2: "Send the header X-API-Key: <token> with every request.",
    step3: "Call the endpoints below. Responses are JSON with snake_case field names; values are returned as stored.",
    baseUrl: "Base URL",
    auth: "Authentication header",
    ep1: "Endpoint 1: Contract lines with packaging / quality requirements (inspection)",
    ep1Desc: "Returns contract header plus every product line under it, including packaging and quality requirements. Exact contract number match.",
    ep2: "Endpoint 2: Product list search",
    ep2Desc: "Supports keyword search, filters and pagination; same structure as the web product list.",
    pathParams: "Path parameters",
    queryParams: "Query parameters",
    respFields: "Response fields",
    itemFields: "Fields of each items[] line",
    example: "curl example",
    errors: "Error codes",
    field: "Field",
    desc: "Description",
    copy: "Copy",
    copied: "Copied",
    contractNo: "Contract number as synced from FileMaker (case-sensitive)",
    q: "Keyword; comma = OR, space = AND",
    q_fields: "Restrict search to fields, comma-separated (e.g. item_no,upc)",
    contract_no_q: "Filter by contract number",
    invoice_no_q: "Filter by invoice number",
    customer_name_q: "Filter by customer name",
    factory_name_q: "Filter by factory name",
    page: "Page number, starts at 1",
    page_size: "Rows per page, max 200",
    e401: "Token missing, invalid, revoked or expired",
    e404: "Contract not found",
    e422: "Malformed parameters",
    inventoryResp: "items (product rows), total (product count), page, page_size, totals (qty / shipping_qty / amount)",
  },
};

const CONTRACT_FIELDS = [
  ["contract_no", "合同号", "Contract number"],
  ["customer_name", "客户名称", "Customer name"],
  ["budget_no", "预算编号", "Budget number"],
  ["po_number", "PO 号", "PO number"],
  ["contract_working_date", "合同作业日期", "Contract working date"],
  ["delivery_date", "交货期", "Delivery date"],
  ["po_shipping_date", "PO 出运日", "PO shipping date"],
  ["shipping_date", "出运日期(单证)", "Shipping date (docs)"],
  ["total_items", "产品行数", "Number of lines"],
  ["total_qty", "数量合计", "Total quantity"],
  ["items", "产品行数组,见下表", "Array of lines, see below"],
];

const ITEM_FIELDS = [
  ["product_no", "产品编号(FileMaker 内部编号)", "Product number (FileMaker)"],
  ["serial_number", "产品序列号", "Product serial number"],
  ["item_no / company_product_no", "公司产品编号", "Company product number"],
  ["customer_product_no", "客户产品编号", "Customer product number"],
  ["gt_sku", "GT SKU", "GT SKU"],
  ["upc", "UPC 条码", "UPC barcode"],
  ["chinese_name", "中文名称", "Chinese name"],
  ["english_name", "英文名称", "English name"],
  ["description", "产品描述", "Description"],
  ["english_description", "英文产品描述", "English description"],
  ["image_url", "图片地址(可直接访问)", "Image URL (direct)"],
  ["size", "规格 / 尺寸", "Size / spec"],
  ["specification", "材质 / 规格说明", "Material / specification"],
  ["package", "包装方式", "Package"],
  ["category", "类别", "Category"],
  ["factory_name", "厂家名称", "Factory name"],
  ["qty", "数量", "Quantity"],
  ["shipping_qty", "出货数量", "Shipping quantity"],
  ["pcs_per_ctn", "装箱率(每箱件数)", "Pieces per carton"],
  ["carton_length_cm / carton_width_cm / carton_height_cm", "外箱长 / 宽 / 高(cm)", "Carton L / W / H (cm)"],
  ["cbm / cbm_total", "单箱体积 / 总体积(m³)", "CBM per carton / total"],
  ["gross_weight_kg / net_weight_kg", "毛重 / 净重(kg)", "Gross / net weight (kg)"],
  ["packaging_requirement", "包装要求", "Packaging requirement"],
  ["quality_requirement", "质检要求", "Quality requirement"],
  ["invoice_no", "发票号", "Invoice number"],
  ["entry_date", "录入日期", "Entry date"],
];

function CodeBlock({ code, testId, copyLabel, copiedLabel }) {
  const copy = () => {
    navigator.clipboard.writeText(code);
    toast.success(copiedLabel);
  };
  return (
    <div className="relative group" data-testid={testId}>
      <pre className="bg-gray-900 text-gray-100 text-xs font-mono p-4 pr-24 overflow-x-auto whitespace-pre-wrap break-all rounded-md">{code}</pre>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testId}-copy`}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-100 rounded transition-colors"
      >
        <Copy size={12} /> {copyLabel}
      </button>
    </div>
  );
}

function FieldTable({ rows, isZh, fieldLabel, descLabel, testId }) {
  return (
    <table className="w-full text-sm border border-gray-200" data-testid={testId}>
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2 w-2/5">{fieldLabel}</th>
          <th className="text-left text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2">{descLabel}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, zh, en]) => (
          <tr key={name} className="border-t border-gray-100">
            <td className="px-3 py-1.5 font-mono text-xs text-gray-800">{name}</td>
            <td className="px-3 py-1.5 text-gray-600">{isZh ? zh : en}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({ title, children, testId }) {
  return (
    <section className="space-y-3" data-testid={testId}>
      <h2 className="text-base md:text-lg font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

export default function ApiDocsPanel() {
  const { i18n } = useTranslation();
  const isZh = (i18n.resolvedLanguage || i18n.language || "en").startsWith("zh");
  const t = isZh ? T.zh : T.en;
  const base = `${window.location.origin}/api`;

  const curlContract = `curl -H "X-API-Key: <YOUR_TOKEN>" \\\n  "${base}/external/contracts/19272956"`;
  const curlInventory = `curl -H "X-API-Key: <YOUR_TOKEN>" \\\n  "${base}/inventory?q=BD019&page=1&page_size=50"`;
  const sampleResp = `{
  "contract_no": "19272956",
  "customer_name": "Better Home Fashion Inc",
  "po_number": "AM2026-01",
  "delivery_date": "08/27/2026",
  "total_items": 1,
  "total_qty": 1200,
  "items": [
    {
      "item_no": "BD019",
      "upc": "6971234567890",
      "chinese_name": "浴垫",
      "english_name": "Bath Mat",
      "image_url": "https://.../208931.png",
      "size": "50x80cm",
      "qty": 1200,
      "pcs_per_ctn": 24,
      "packaging_requirement": "...",
      "quality_requirement": "..."
    }
  ]
}`;

  const queryRows = [
    ["q", t.q, t.q],
    ["q_fields", t.q_fields, t.q_fields],
    ["contract_no", t.contract_no_q, t.contract_no_q],
    ["invoice_no", t.invoice_no_q, t.invoice_no_q],
    ["customer_name", t.customer_name_q, t.customer_name_q],
    ["factory_name", t.factory_name_q, t.factory_name_q],
    ["page", t.page, t.page],
    ["page_size", t.page_size, t.page_size],
  ];
  const errorRows = [
    ["401 Unauthorized", t.e401, t.e401],
    ["404 Not Found", t.e404, t.e404],
    ["422 Unprocessable Entity", t.e422, t.e422],
  ];

  return (
    <div className="space-y-10 bg-white border border-gray-200 p-6" data-testid="api-docs-panel">
      <div className="space-y-2">
        <h1 className="font-heading text-xl font-bold text-gray-900" data-testid="api-docs-title">{t.title}</h1>
        <p className="text-sm text-gray-500">{t.intro}</p>
      </div>

      <Section title={t.steps} testId="api-docs-steps">
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1">
          <li>{t.step1}</li>
          <li>{t.step2}</li>
          <li>{t.step3}</li>
        </ol>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">{t.baseUrl}</p>
            <CodeBlock code={base} testId="api-docs-base-url" copyLabel={t.copy} copiedLabel={t.copied} />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t.auth}</p>
            <CodeBlock code="X-API-Key: <YOUR_TOKEN>" testId="api-docs-auth-header" copyLabel={t.copy} copiedLabel={t.copied} />
          </div>
        </div>
      </Section>

      <Section title={t.ep1} testId="api-docs-endpoint-contract">
        <p className="text-sm text-gray-600">{t.ep1Desc}</p>
        <CodeBlock code={`GET ${base}/external/contracts/{contract_no}`} testId="api-docs-contract-url" copyLabel={t.copy} copiedLabel={t.copied} />
        <p className="text-xs text-gray-500">{t.pathParams}</p>
        <FieldTable rows={[["contract_no", t.contractNo, t.contractNo]]} isZh={isZh} fieldLabel={t.field} descLabel={t.desc} testId="api-docs-contract-path-table" />
        <p className="text-xs text-gray-500">{t.example}</p>
        <CodeBlock code={curlContract} testId="api-docs-contract-curl" copyLabel={t.copy} copiedLabel={t.copied} />
        <p className="text-xs text-gray-500">{t.respFields}</p>
        <FieldTable rows={CONTRACT_FIELDS} isZh={isZh} fieldLabel={t.field} descLabel={t.desc} testId="api-docs-contract-resp-table" />
        <p className="text-xs text-gray-500">{t.itemFields}</p>
        <FieldTable rows={ITEM_FIELDS} isZh={isZh} fieldLabel={t.field} descLabel={t.desc} testId="api-docs-contract-item-table" />
        <CodeBlock code={sampleResp} testId="api-docs-contract-sample" copyLabel={t.copy} copiedLabel={t.copied} />
      </Section>

      <Section title={t.ep2} testId="api-docs-endpoint-inventory">
        <p className="text-sm text-gray-600">{t.ep2Desc}</p>
        <CodeBlock code={`GET ${base}/inventory`} testId="api-docs-inventory-url" copyLabel={t.copy} copiedLabel={t.copied} />
        <p className="text-xs text-gray-500">{t.queryParams}</p>
        <FieldTable rows={queryRows} isZh={isZh} fieldLabel={t.field} descLabel={t.desc} testId="api-docs-inventory-query-table" />
        <p className="text-xs text-gray-500">{t.example}</p>
        <CodeBlock code={curlInventory} testId="api-docs-inventory-curl" copyLabel={t.copy} copiedLabel={t.copied} />
        <p className="text-xs text-gray-500">{t.respFields}</p>
        <p className="text-sm text-gray-600 font-mono text-xs">{t.inventoryResp}</p>
      </Section>

      <Section title={t.errors} testId="api-docs-errors">
        <FieldTable rows={errorRows} isZh={isZh} fieldLabel={t.field} descLabel={t.desc} testId="api-docs-errors-table" />
      </Section>
    </div>
  );
}
